/**
 * Smart Transaction Grouping Utility
 * 
 * Groups transactions using a hybrid approach:
 * 1. Keyword detection for known patterns (ATM, PAYROLL, etc.)
 * 2. Fuzzy matching for similar descriptions (>70% similarity)
 * 3. Advanced similarity algorithms (TF-IDF, N-gram, Phonetic)
 * 
 * This reduces the number of groups users need to categorize manually.
 */

import { 
  calculateAdvancedSimilarity, 
  normalizeForRestaurant,
  globalTFIDFIndex,
  type SimilarityResult 
} from './advancedSimilarity';
import { vendorNameMatcher, extractVendorFromDescription } from './vendorNameMatcher';

export interface Transaction {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  category_type?: 'revenue' | 'expenses' | 'balance_sheet' | null;
  subcategory?: string | null;
  detail_source?: string | null;
  location_id?: string;
  period_flag: string;
  account_id?: string;
  account_name?: string;
  is_credit_card?: boolean; // For credit cards, sign convention is INVERTED
}

export interface TransactionGroup {
  descriptionPattern: string;
  transactions: Transaction[];
  isTrained: boolean;
  isRevenue?: boolean;
}

/**
 * Determines the effective transaction type based on amount AND account type
 * This is CRITICAL for grouping - we must never mix revenue transactions with expense transactions
 * 
 * For CREDIT CARDS, the sign convention is INVERTED:
 * - Positive amount on CC = Charge/Purchase = EXPENSE
 * - Negative amount on CC = Payment/Refund = BALANCE_SHEET (not revenue!)
 * 
 * For REGULAR BANK ACCOUNTS:
 * - Positive amount = Deposit = REVENUE
 * - Negative amount = Withdrawal = EXPENSE
 */
function getEffectiveTransactionType(tx: Transaction): 'revenue' | 'expense' {
  if (tx.is_credit_card) {
    // Credit card: positive = expense, negative = balance_sheet (treat as expense for grouping)
    // We never want CC transactions grouped with regular bank revenue
    return 'expense';
  }
  // Regular account: positive = revenue, negative = expense
  return tx.amount >= 0 ? 'revenue' : 'expense';
}

/**
 * Known banking keywords that should group transactions together
 * These are common patterns that appear in transaction descriptions
 */
const GROUPING_KEYWORDS: Array<{ keywords: string[]; group: string }> = [
  { keywords: ['atm', 'cash withdrawal', 'cash out', 'atm withdrawal', 'atm cash'], group: 'ATM' },
  { keywords: ['payroll', 'adp', 'gusto', 'paychex', 'quickbooks payroll', 'payroll pymt'], group: 'PAYROLL' },
  { keywords: ['doordash', 'door dash', 'dd', 'doordash inc'], group: 'DOORDASH' },
  { keywords: ['uber eats', 'ubereats', 'uber'], group: 'UBER_EATS' },
  { keywords: ['grubhub', 'grub hub', 'gh'], group: 'GRUBHUB' },
  { keywords: ['postmates', 'post mates'], group: 'POSTMATES' },
  { keywords: ['godaddy', 'go daddy', 'dnh*godaddy'], group: 'GODADDY' },
  { keywords: ['tax pymt', 'tax payment', 'irs', 'webfile tax', 'tax payment', 'federal tax', 'state tax'], group: 'TAX' },
  { keywords: ['rent', 'lease payment', 'landlord'], group: 'RENT' },
  { keywords: ['electric', 'power', 'gas bill', 'water bill', 'utility', 'pge', 'edison', 'duke energy', 'propane'], group: 'UTILITIES' },
  { keywords: ['toast', 'toast deposit', 'toast payment', 'tst'], group: 'TOAST' },
  { keywords: ['square', 'square deposit', 'square payment'], group: 'SQUARE' },
  { keywords: ['stripe', 'stripe deposit', 'stripe payment'], group: 'STRIPE' },
  { keywords: ['transfer from', 'transfer to', 'internal transfer', 'wire transfer', 'ach transfer'], group: 'TRANSFER' },
  { keywords: ['loan payment', 'loan pymt', 'sba', 'line of credit', 'loc payment'], group: 'LOAN' },
  { keywords: ['interest income', 'interest earned', 'interest payment', 'interest charge'], group: 'INTEREST' },
  { keywords: ['insurance', 'ins prem', 'premium', 'liability ins', 'workers comp'], group: 'INSURANCE' },
  { keywords: ['citizens', 'citizens bank'], group: 'CITIZENS_BANK' },
  { keywords: ['chase', 'chase bank'], group: 'CHASE_BANK' },
  { keywords: ['bank of america', 'bofa', 'boa'], group: 'BOA' },
  { keywords: ['wells fargo', 'wells'], group: 'WELLS_FARGO' },
];

/** Set of keyword group names for fast lookup (used to avoid merging keyword groups with unrelated pattern groups) */
const KEYWORD_GROUP_NAMES = new Set(GROUPING_KEYWORDS.map((g) => g.group));

/**
 * Extracts vendor/company name from description
 * Looks for patterns like "ORIG CO NAME:", "COMPANY:", "MERCHANT:", etc.
 */
function extractVendorName(desc: string): string | null {
  const lower = desc.toLowerCase();
  
  // Common patterns for vendor names in bank descriptions
  const patterns = [
    /orig\s+co\s+name[:\s]+([^,\n]+)/i,
    /company[:\s]+([^,\n]+)/i,
    /merchant[:\s]+([^,\n]+)/i,
    /vendor[:\s]+([^,\n]+)/i,
    /^([A-Z][A-Z\s&]+?)(?:\s+INC|\s+LLC|\s+INC\.|\s+LLC\.|\s+LTD|\s+LTD\.|,|\s+#|\s+\d)/, // Company name at start
  ];
  
  for (const pattern of patterns) {
    const match = desc.match(pattern);
    if (match && match[1]) {
      return match[1].trim().toLowerCase();
    }
  }
  
  return null;
}

/**
 * Normalizes a description for comparison
 * Preserves vendor/company names but removes transaction IDs and numbers
 * Improved to preserve more meaningful information for better grouping
 */
function normalizeDescription(desc: string): string {
  if (!desc) return '';
  
  // First, try to extract vendor name
  const vendorName = extractVendorName(desc);
  
  // Normalize the description - be less aggressive with removal
  let normalized = desc
    .toLowerCase()
    // Remove transaction IDs and reference numbers (but preserve vendor names)
    .replace(/\b(orig\s+id|ref|reference|transaction\s+id|txn\s+id|confirmation|conf)[:\s]+[A-Z0-9-]+\b/gi, '')
    .replace(/\b[A-Z0-9]{12,}\b/g, '') // Only remove very long alphanumeric IDs (12+ chars)
    .replace(/\b\d{4,}\b/g, '') // Remove long number sequences (4+ digits)
    .replace(/[#*]+/g, '') // Remove special chars but keep punctuation that might be meaningful
    .replace(/\s+/g, ' ')
    .trim();
  
  // If we found a vendor name, prepend it to ensure vendor-based grouping
  if (vendorName) {
    normalized = `${vendorName} ${normalized}`;
  }
  
  // Preserve more context - increased from 80 to 120 characters
  const result = normalized.substring(0, 120);
  
  // Debug logging for grouping issues (can be removed in production)
  if (process.env.NODE_ENV === 'development') {
    // Only log if descriptions are similar but might not group
    // This helps identify normalization issues
    if (desc.length > 20 && desc !== result) {
      // Log significant normalizations
      console.debug('[normalizeDescription]', {
        original: desc.substring(0, 60) + (desc.length > 60 ? '...' : ''),
        normalized: result,
        vendorName: vendorName || 'none',
        lengthDiff: desc.length - result.length
      });
    }
  }
  
  return result;
}

/**
 * Detects if a description matches a known keyword group
 * Returns the group name if matched, null otherwise
 * Also checks extracted vendor names for better matching
 */
function detectKeywordGroup(description: string): string | null {
  const normalized = description.toLowerCase();
  
  // First, try to extract vendor name and check if it matches a keyword
  const vendorName = extractVendorName(description);
  if (vendorName) {
    for (const { keywords, group } of GROUPING_KEYWORDS) {
      for (const keyword of keywords) {
        if (vendorName.includes(keyword.toLowerCase()) || keyword.toLowerCase().includes(vendorName)) {
          return group;
        }
      }
    }
  }
  
  // Fall back to checking the full description
  for (const { keywords, group } of GROUPING_KEYWORDS) {
    for (const keyword of keywords) {
      if (normalized.includes(keyword.toLowerCase())) {
        return group;
      }
    }
  }
  
  return null;
}

// Similarity cache to avoid recalculating the same comparisons
const similarityCache = new Map<string, number>();
const CACHE_SIZE_LIMIT = 10000; // Limit cache size to prevent memory issues

/**
 * Generate cache key for similarity calculation
 */
function getCacheKey(str1: string, str2: string): string {
  // Sort strings to ensure consistent cache key regardless of order
  const [s1, s2] = [str1.toLowerCase(), str2.toLowerCase()].sort();
  return `${s1}|||${s2}`;
}

/**
 * Jaro-Winkler similarity for vendor names (more efficient and accurate for names)
 * Returns similarity score between 0 and 1
 */
function jaroWinklerSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 1.0;
  if (s1.length === 0 || s2.length === 0) return 0.0;
  
  // Jaro distance
  const matchWindow = Math.floor(Math.max(s1.length, s2.length) / 2) - 1;
  const s1Matches = new Array(s1.length).fill(false);
  const s2Matches = new Array(s2.length).fill(false);
  
  let matches = 0;
  let transpositions = 0;
  
  // Find matches
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, s2.length);
    
    for (let j = start; j < end; j++) {
      if (s2Matches[j] || s1[i] !== s2[j]) continue;
      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }
  
  if (matches === 0) return 0.0;
  
  // Find transpositions
  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }
  
  const jaro = (matches / s1.length + matches / s2.length + (matches - transpositions / 2) / matches) / 3.0;
  
  // Winkler modification (prefix bonus)
  let prefix = 0;
  const maxPrefix = Math.min(4, Math.min(s1.length, s2.length));
  for (let i = 0; i < maxPrefix; i++) {
    if (s1[i] === s2[i]) prefix++;
    else break;
  }
  
  return jaro + (0.1 * prefix * (1 - jaro));
}

/**
 * Optimized Levenshtein distance with early exit for very different strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Early exit: if length difference is too large, similarity will be low
  const maxLen = Math.max(len1, len2);
  if (maxLen === 0) return 0;
  const minLen = Math.min(len1, len2);
  if (maxLen > minLen * 2) {
    // Strings are too different, return max distance
    return maxLen;
  }
  
  // Use only 2 rows instead of full matrix for memory efficiency
  let prevRow = Array(len2 + 1).fill(0).map((_, i) => i);
  let currRow = Array(len2 + 1).fill(0);
  
  for (let i = 1; i <= len1; i++) {
    currRow[0] = i;
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        currRow[j] = prevRow[j - 1];
      } else {
        currRow[j] = Math.min(
          prevRow[j] + 1,
          currRow[j - 1] + 1,
          prevRow[j - 1] + 1
        );
      }
    }
    [prevRow, currRow] = [currRow, prevRow];
  }
  
  return prevRow[len2];
}

/**
 * Calculates similarity score between two strings (0-1)
 * Uses cached results and advanced similarity algorithms
 * 
 * Now uses: TF-IDF weighting, N-gram matching, Phonetic similarity
 */
function calculateSimilarity(str1: string, str2: string, useJaroWinkler: boolean = false): number {
  if (str1 === str2) return 1.0;
  
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  // Check cache first
  const cacheKey = getCacheKey(s1, s2);
  if (similarityCache.has(cacheKey)) {
    return similarityCache.get(cacheKey)!;
  }
  
  let similarity: number;
  
  if (useJaroWinkler) {
    // Use Jaro-Winkler for vendor names (better for short strings with common prefixes)
    similarity = jaroWinklerSimilarity(s1, s2);
  } else {
    // Use advanced similarity (combines TF-IDF, N-gram, Phonetic)
    const result = calculateAdvancedSimilarity(s1, s2, {
      domainSpecific: true,
      tokenWeight: 0.35,
      nGramWeight: 0.35,
      phoneticWeight: 0.30
    });
    similarity = result.score;
  }
  
  // Cache result (with size limit)
  if (similarityCache.size < CACHE_SIZE_LIMIT) {
    similarityCache.set(cacheKey, similarity);
  }
  
  return similarity;
}

/**
 * Calculate similarity using vendor-aware matching
 * Uses vendorNameMatcher for better vendor recognition
 */
function calculateVendorAwareSimilarity(desc1: string, desc2: string): number {
  // Try to extract vendor names
  const vendor1 = extractVendorFromDescription(desc1);
  const vendor2 = extractVendorFromDescription(desc2);
  
  // If both have vendors, use vendor matcher
  if (vendor1 && vendor2) {
    const vendorResult = vendorNameMatcher.matchVendors(vendor1, vendor2);
    if (vendorResult.matched) {
      return vendorResult.confidence;
    }
  }
  
  // Fall back to advanced similarity
  return calculateSimilarity(desc1, desc2, false);
}

/**
 * Checks if two groups should be merged based on similarity
 * Groups are merged if:
 * 1. Similarity > threshold (adjusted for better grouping)
 * 2. Both groups have the same EFFECTIVE transaction type (revenue vs expense)
 *    - This considers credit card sign inversion!
 * 3. Both groups have the same vendor name (if extractable)
 * 
 * Special handling: Singleton groups (1 transaction) get more lenient thresholds
 */
function shouldMergeGroups(
  group1: TransactionGroup,
  group2: TransactionGroup,
  similarityThreshold: number = 0.7,
  isCombinedAccounts: boolean = false
): boolean {
  // Adjusted thresholds to reduce singleton groups while maintaining accuracy
  // Combined accounts use slightly higher threshold to avoid false groupings across accounts
  let effectiveThreshold = isCombinedAccounts ? 0.70 : 0.65;
  
  // Special handling for singleton groups: apply more lenient threshold
  const isGroup1Singleton = group1.transactions.length === 1;
  const isGroup2Singleton = group2.transactions.length === 1;
  const hasSingleton = isGroup1Singleton || isGroup2Singleton;
  
  // If either group is a singleton, reduce threshold by 0.05-0.10 to allow more merging
  if (hasSingleton) {
    effectiveThreshold -= 0.05; // More lenient for singletons
  }
  
  // CRITICAL: Check effective transaction type first (accounts for credit card inversion)
  const tx1 = group1.transactions[0];
  const tx2 = group2.transactions[0];
  if (!tx1 || !tx2) return false;
  
  const type1 = getEffectiveTransactionType(tx1);
  const type2 = getEffectiveTransactionType(tx2);
  
  // NEVER merge groups with different effective types (revenue vs expense)
  if (type1 !== type2) {
    return false;
  }

  // CRITICAL: Never merge a keyword group (e.g. INTEREST, ATM) with a different group.
  // Keyword groups are built from phrases like "interest charge"; if the other group's
  // pattern were the same, it would already be the same keyword. This prevents
  // "PURCHASE INTEREST CHARGE" from being grouped with unrelated descriptions (e.g. "Porkys Food Truc").
  const pattern1 = group1.descriptionPattern;
  const pattern2 = group2.descriptionPattern;
  if (KEYWORD_GROUP_NAMES.has(pattern1) && pattern2 !== pattern1) return false;
  if (KEYWORD_GROUP_NAMES.has(pattern2) && pattern1 !== pattern2) return false;

  // Extract vendor names from first transaction in each group using improved extraction
  const vendor1 = group1.transactions[0]?.description 
    ? extractVendorFromDescription(group1.transactions[0].description) 
    : null;
  const vendor2 = group2.transactions[0]?.description 
    ? extractVendorFromDescription(group2.transactions[0].description) 
    : null;
  
  // If both have vendor names, use vendor matcher for intelligent matching
  if (vendor1 && vendor2) {
    const vendorResult = vendorNameMatcher.matchVendors(vendor1, vendor2);
    if (vendorResult.matched) {
      // Vendors match - proceed with merge
      return true;
    } else if (vendorResult.confidence < 0.5) {
      // Vendors clearly don't match
      return false;
    }
    // Uncertain vendor match - continue with description similarity
  } else if (vendor1 || vendor2) {
    // One has vendor name, other doesn't - be more cautious
    // Only merge if similarity is very high
    const similarity = calculateVendorAwareSimilarity(
      group1.descriptionPattern, 
      group2.descriptionPattern
    );
    if (similarity < 0.80) {
      return false;
    }
  }
  
  // Check similarity using normalized descriptions from actual transactions
  // Use domain-specific normalization for better matching
  const desc1 = normalizeForRestaurant(
    group1.transactions[0]?.description || group1.descriptionPattern
  );
  const desc2 = normalizeForRestaurant(
    group2.transactions[0]?.description || group2.descriptionPattern
  );
  
  // Early exit: if normalized descriptions are identical, merge immediately
  if (desc1 === desc2 && desc1.length > 0) {
    return true;
  }
  
  // Use advanced similarity with domain-specific normalization
  const result = calculateAdvancedSimilarity(desc1, desc2, {
    domainSpecific: false, // Already normalized
    tokenWeight: 0.35,
    nGramWeight: 0.35,
    phoneticWeight: 0.30
  });
  
  if (result.score < effectiveThreshold) {
    return false;
  }

  // Require at least one significant shared word (length >= 4) so we don't merge
  // descriptions that are semantically different but score high on n-gram/phonetic similarity.
  const words1 = new Set(desc1.split(/\s+/).filter((w) => w.length >= 4));
  const words2 = new Set(desc2.split(/\s+/).filter((w) => w.length >= 4));
  const hasSharedWord = [...words1].some((w) => words2.has(w));
  if (!hasSharedWord) return false;

  return true;
}

/**
 * Initialize the TF-IDF index with transaction descriptions for better similarity matching
 * Should be called before smartGroupTransactions for optimal results
 */
export function initializeTransactionIndex(transactions: Transaction[]): void {
  const descriptions = transactions
    .map(t => t.description || '')
    .filter(d => d.length > 0);
  
  if (descriptions.length > 0) {
    globalTFIDFIndex.buildIndex(descriptions, true);
  }
}

/**
 * Smart grouping algorithm that combines keyword detection and fuzzy matching
 * IMPORTANT: Transactions with different EFFECTIVE types (revenue vs expense) are NEVER grouped together
 * This accounts for credit card sign inversion where positive = expense, not revenue
 * 
 * Uses advanced similarity algorithms:
 * - TF-IDF for corpus-aware term weighting
 * - N-gram for character-level similarity
 * - Phonetic matching for sound-alike vendors
 * - Domain-specific normalization for restaurant industry
 * 
 * @param transactions - Array of transactions to group
 * @param isCombinedAccounts - If true, uses stricter similarity thresholds to reduce false groupings
 */
export function smartGroupTransactions(
  transactions: Transaction[], 
  isCombinedAccounts: boolean = false
): TransactionGroup[] {
  // Initialize TF-IDF index for this batch
  initializeTransactionIndex(transactions);
  // Debug logging for cross-account grouping issues
  const debugLogging = process.env.NODE_ENV === 'development';
  const groupingDebug: Array<{
    account: string;
    description: string;
    normalized: string;
    key: string;
    effectiveType: string;
  }> = [];
  
  // Step 1: Initial grouping by keyword or normalized pattern, SEPARATED BY EFFECTIVE TYPE
  // Use a composite key: "keyword_type" or "pattern_type" to ensure different types are never grouped
  // CRITICAL: Use effective type (accounts for credit card inversion) not raw amount sign
  // NOTE: Key does NOT include account_id, so transactions with same description across accounts will group
  const keywordGroups = new Map<string, Transaction[]>();
  const patternGroups = new Map<string, Transaction[]>();
  
  for (const tx of transactions) {
    // Use EFFECTIVE type that accounts for credit card sign inversion
    const effectiveType = getEffectiveTransactionType(tx); // 'revenue' or 'expense'
    const keyword = detectKeywordGroup(tx.description || '');
    
    if (keyword) {
      // Group by keyword AND effective type - ensures revenue and expense never mix
      const key = `${keyword}_${effectiveType}`;
      if (!keywordGroups.has(key)) {
        keywordGroups.set(key, []);
      }
      keywordGroups.get(key)!.push(tx);
      
      if (debugLogging) {
        groupingDebug.push({
          account: tx.account_name || tx.account_id || 'unknown',
          description: (tx.description || '').substring(0, 50),
          normalized: keyword,
          key,
          effectiveType
        });
      }
    } else {
      // Group by normalized pattern AND effective type - ensures revenue and expense never mix
      const pattern = normalizeDescription(tx.description || '');
      const key = `${pattern}_${effectiveType}`;
      if (!patternGroups.has(key)) {
        patternGroups.set(key, []);
      }
      patternGroups.get(key)!.push(tx);
      
      if (debugLogging) {
        groupingDebug.push({
          account: tx.account_name || tx.account_id || 'unknown',
          description: (tx.description || '').substring(0, 50),
          normalized: pattern.substring(0, 50),
          key: key.substring(0, 60),
          effectiveType
        });
      }
    }
  }
  
  // Log grouping summary for debugging cross-account grouping
  if (debugLogging && isCombinedAccounts) {
    // Find patterns that appear across multiple accounts but might not be grouping
    const patternByAccount = new Map<string, Set<string>>();
    groupingDebug.forEach(entry => {
      if (!patternByAccount.has(entry.normalized)) {
        patternByAccount.set(entry.normalized, new Set());
      }
      patternByAccount.get(entry.normalized)!.add(entry.account);
    });
    
    // Log patterns that appear in multiple accounts
    const crossAccountPatterns = Array.from(patternByAccount.entries())
      .filter(([_, accounts]) => accounts.size > 1)
      .map(([pattern, accounts]) => ({
        pattern: pattern.substring(0, 60),
        accounts: Array.from(accounts),
        accountCount: accounts.size
      }));
    
    if (crossAccountPatterns.length > 0) {
      console.log('[smartGroupTransactions] Cross-account patterns detected:', {
        isCombinedAccounts: isCombinedAccounts,
        totalTransactions: transactions.length,
        crossAccountPatterns: crossAccountPatterns.slice(0, 10), // Log first 10
        totalPatterns: patternByAccount.size,
        crossAccountPatternCount: crossAccountPatterns.length
      });
    }
  }
  
  // Step 2: Convert to TransactionGroup array
  const groups: TransactionGroup[] = [];
  
  // Add keyword-based groups
  for (const [key, txs] of keywordGroups.entries()) {
    const firstTx = txs[0];
    // Extract keyword from key (remove _revenue or _expense suffix)
    const keyword = key.replace(/_revenue$|_expense$/, '');
    const effectiveType = getEffectiveTransactionType(firstTx);
    groups.push({
      descriptionPattern: keyword,
      transactions: txs,
      isTrained: false,
      isRevenue: effectiveType === 'revenue',
    });
  }
  
  // Add pattern-based groups
  for (const [key, txs] of patternGroups.entries()) {
    const firstTx = txs[0];
    // Extract pattern from key (remove _revenue or _expense suffix)
    const pattern = key.replace(/_revenue$|_expense$/, '');
    const effectiveType = getEffectiveTransactionType(firstTx);
    groups.push({
      descriptionPattern: pattern,
      transactions: txs,
      isTrained: false,
      isRevenue: effectiveType === 'revenue',
    });
  }
  
  // Step 3: Fuzzy merge pass - merge similar groups (optimized with early exits)
  // NOTE: shouldMergeGroups already checks for same effective type, but we add an extra safety check here
  let mergedGroups: TransactionGroup[] = [...groups];
  
  // For large transaction sets (>1000), consider using Web Workers
  // For now, optimize the merge algorithm with better early exits
  const shouldUseOptimizedMerge = transactions.length > 1000;
  
  let hasChanges = true;
  let passCount = 0;
  // Increased passes to catch groups that become similar after initial merges
  // More passes help reduce singleton groups by finding matches that emerge after earlier merges
  const maxPasses = shouldUseOptimizedMerge ? 3 : 5;
  
  // Multiple merge passes to catch groups that become similar after initial merges
  while (hasChanges && passCount < maxPasses) {
    hasChanges = false;
    passCount++;
    const newMergedGroups: TransactionGroup[] = [];
    const merged = new Set<number>();
    
    // Sort groups by size (larger first) to merge larger groups first
    const sortedIndices = Array.from({ length: mergedGroups.length }, (_, i) => i)
      .sort((a, b) => mergedGroups[b].transactions.length - mergedGroups[a].transactions.length);
    
    for (const i of sortedIndices) {
      if (merged.has(i)) continue;
      
      let currentGroup = mergedGroups[i];
      const currentType = currentGroup.isRevenue; // true = revenue, false = expense
      
      // Try to merge with other groups (only check unmerged groups)
      for (const j of sortedIndices) {
        if (i >= j || merged.has(j)) continue; // Only check groups after current in sorted order
        
        const otherGroup = mergedGroups[j];
        const otherType = otherGroup.isRevenue;
        
        // CRITICAL: Never merge groups with different effective types (revenue vs expense)
        if (currentType !== otherType) {
          continue; // Skip merging if types differ
        }
        
        // Early exit: if groups are too different in size, skip (heuristic)
        // BUT: Allow singleton groups (1 transaction) to merge with larger groups
        // Only apply size ratio check when BOTH groups are large (both > 5 transactions)
        if (shouldUseOptimizedMerge) {
          const currentSize = currentGroup.transactions.length;
          const otherSize = otherGroup.transactions.length;
          
          // Skip size ratio check if either group is a singleton (allows singletons to merge)
          if (currentSize > 1 && otherSize > 1) {
            const sizeRatio = Math.min(
              currentSize / otherSize,
              otherSize / currentSize
            );
            // Only skip if both groups are large AND very different in size
            if (sizeRatio < 0.1 && currentSize > 5 && otherSize > 5) {
              // Groups are very different in size and both are large, likely not the same
              continue;
            }
          }
        }
        
        if (shouldMergeGroups(currentGroup, otherGroup, 0.7, isCombinedAccounts)) {
          // Merge group j into currentGroup
          // Use the pattern from the larger group, or the first one if equal
          const betterPattern = currentGroup.transactions.length >= otherGroup.transactions.length
            ? currentGroup.descriptionPattern
            : otherGroup.descriptionPattern;
          
          currentGroup = {
            descriptionPattern: betterPattern,
            transactions: [...currentGroup.transactions, ...otherGroup.transactions],
            isTrained: false,
            isRevenue: currentType, // Keep the first group's type
          };
          merged.add(j);
          hasChanges = true;
          
          // Early exit: if we've merged enough, stop checking (for large sets)
          if (shouldUseOptimizedMerge && merged.size > mergedGroups.length * 0.5) {
            break;
          }
        }
      }
      
      newMergedGroups.push(currentGroup);
      merged.add(i);
    }
    
    mergedGroups = newMergedGroups;
    
    // Clear cache periodically to prevent memory issues
    if (passCount % 2 === 0 && similarityCache.size > CACHE_SIZE_LIMIT * 0.8) {
      // Clear half of the cache (oldest entries)
      const entries = Array.from(similarityCache.entries());
      similarityCache.clear();
      // Keep the most recent half
      entries.slice(-Math.floor(entries.length / 2)).forEach(([key, value]) => {
        similarityCache.set(key, value);
      });
    }
  }
  
  // Step 4: Validate and filter - ensure no group has mixed effective types (safety check)
  const validatedGroups = mergedGroups.map(group => {
    // Verify all transactions in group have the same effective type
    const firstTx = group.transactions[0];
    if (!firstTx) return { ...group, transactions: [] };
    
    const firstType = getEffectiveTransactionType(firstTx);
    
    // Filter out any transactions with different effective type (shouldn't happen, but safety check)
    const sameTypeTransactions = group.transactions.filter(tx => {
      const txType = getEffectiveTransactionType(tx);
      return txType === firstType;
    });
    
    // If we filtered any out, log a warning
    if (sameTypeTransactions.length !== group.transactions.length) {
      console.warn(`Filtered ${group.transactions.length - sameTypeTransactions.length} transactions with different effective type from group: ${group.descriptionPattern}`);
    }
    
    return {
      ...group,
      transactions: sameTypeTransactions,
      isRevenue: firstType === 'revenue',
    };
  }).filter(group => group.transactions.length > 0); // Remove empty groups
  
  // Step 5: Sort by transaction count (most frequent first)
  validatedGroups.sort((a, b) => b.transactions.length - a.transactions.length);
  
  // Debug logging: Show final group statistics, especially for cross-account grouping
  if (debugLogging && isCombinedAccounts && validatedGroups.length > 0) {
    // Analyze groups to see which ones span multiple accounts
    const crossAccountGroups = validatedGroups
      .map(group => {
        const accounts = new Set(group.transactions.map(tx => tx.account_name || tx.account_id || 'unknown'));
        return {
          pattern: group.descriptionPattern.substring(0, 50),
          transactionCount: group.transactions.length,
          accountCount: accounts.size,
          accounts: Array.from(accounts),
          isCrossAccount: accounts.size > 1
        };
      })
      .filter(g => g.isCrossAccount);
    
    console.log('[smartGroupTransactions] Final grouping summary:', {
      isCombinedAccounts: isCombinedAccounts,
      totalGroups: validatedGroups.length,
      totalTransactions: transactions.length,
      crossAccountGroups: crossAccountGroups.length,
      crossAccountGroupDetails: crossAccountGroups.slice(0, 10), // Show first 10 cross-account groups
      avgTransactionsPerGroup: (transactions.length / validatedGroups.length).toFixed(2)
    });
  }
  
  return validatedGroups;
}

